// §10.1 — the clipboard.
//
// "Shell-owned ring of the last 25 entries, persisted for the session only. Writes are free.
// **Reads require a shell-routed user gesture** — the shell owns the paste chord, and delivers the
// payload to the focused app; an app calling `readClipboard()` without a preceding gesture gets
// `PERMISSION_REQUIRES_GESTURE`."
//
// The asymmetry is the whole design. A write tells the shell something the app already had. A read
// takes something the app never had and may never have been meant to see — the user's password
// manager output, the paragraph they copied out of a private document. So a read is not a
// permission an app holds; it is a **thing the user just did**, and it is spent by being used.
import { HangarApiError, type Payload } from '@hangar/shared';

/** §10.1 — the last 25 entries, and no more. */
export const CLIPBOARD_HISTORY_LIMIT = 25;

export interface ClipboardEntry {
  id: string;
  /** Newest first in `history()`. */
  at: number;
  /** Who put it there. A built-in and an external app are recorded the same way. */
  appId: string;
  payload: Payload;
  /** One line for the history panel. Never the bytes themselves. */
  preview: string;
}

export interface ClipboardService {
  /** §10.1 — writes are free. Returns the entry that was pushed. */
  copy(payload: Payload, appId: string, at: number): ClipboardEntry;
  /** The ring, newest first. */
  history(): ClipboardEntry[];
  /**
   * §10.1 — the user did the gesture. The named window may read **once**, and gets `entryId` if
   * one was chosen from the history panel, otherwise the top of the ring.
   */
  armGesture(instanceKey: string, entryId?: string): boolean;
  /**
   * §4.1 `transfer.readClipboard`. Throws `PERMISSION_REQUIRES_GESTURE` unless the shell armed
   * this window, and spends the gesture either way it is used.
   */
  read(instanceKey: string): Payload;
  /** Whether this window could read right now. For the Inspector and the tests. */
  armed(instanceKey: string): boolean;
  clear(): void;
  subscribe(onChange: () => void): () => void;
  /**
   * §10.1 — listen for the user's paste on the shell's own document. Returns the detach.
   *
   * **A DOM `paste` event, not a keymap binding** (D-134). §6.4's reserved set is the set of chords
   * apps must *forward instead of acting on*, and reserving `Ctrl+V` would stop every text field in
   * every app from pasting. The `paste` event fires alongside the browser's own handling rather
   * than instead of it, so the shell learns the user pasted without taking the paste away.
   */
  attach(target: DomListenerTarget): () => void;
}

/** The narrow slice of `document` the gesture listener needs. */
export interface DomListenerTarget {
  addEventListener(type: string, listener: () => void, options?: { capture?: boolean }): void;
  removeEventListener(type: string, listener: () => void, options?: { capture?: boolean }): void;
}

/** A short, safe description of a payload — the only thing the history panel ever shows. */
export function previewOf(payload: Payload, limit = 80): string {
  const first = payload.items[0];
  if (!first) return 'Empty';
  if (typeof first.text === 'string') {
    const flat = first.text.replace(/\s+/g, ' ').trim();
    if (flat.length === 0) return `${first.mime} (empty)`;
    return flat.length > limit ? `${flat.slice(0, limit - 1)}…` : flat;
  }
  if (typeof first.path === 'string') return first.path;
  return `${first.mime}${payload.items.length > 1 ? ` +${payload.items.length - 1}` : ''}`;
}

export interface ClipboardOptions {
  limit?: number;
  /** Injected so ids are deterministic in a test. */
  newId?: () => string;
  /**
   * The window instance a paste is aimed at — the focused one. Injected because "focused" is the
   * window manager's answer and this module may not import it (§4.2).
   */
  focusedInstance?: () => string | null;
}

export function createClipboardService(options: ClipboardOptions = {}): ClipboardService {
  const limit = options.limit ?? CLIPBOARD_HISTORY_LIMIT;
  const ring: ClipboardEntry[] = [];
  const listeners = new Set<() => void>();
  /** Windows the shell has armed, and which entry each was armed for. */
  const armed = new Map<string, string | undefined>();
  let counter = 0;
  const newId = options.newId ?? (() => `clip${(counter += 1)}`);

  const announce = (): void => {
    for (const listener of [...listeners]) listener();
  };

  const service: ClipboardService = {
    copy(payload, appId, at) {
      const entry: ClipboardEntry = {
        id: newId(),
        at,
        appId,
        // Copied on the way in: the ring outlives the call, and the caller's object does not
        // stop being theirs to edit.
        payload: { items: payload.items.map((item) => ({ ...item })) },
        preview: previewOf(payload),
      };
      ring.unshift(entry);
      // §10.1 — "the last 25". The 26th pushes the oldest out, which is what a ring is.
      if (ring.length > limit) ring.length = limit;
      announce();
      return entry;
    },

    history: () => ring.map((entry) => ({ ...entry })),

    armGesture(instanceKey, entryId) {
      if (ring.length === 0) return false;
      if (entryId !== undefined && !ring.some((entry) => entry.id === entryId)) return false;
      armed.set(instanceKey, entryId);
      return true;
    },

    armed: (instanceKey) => armed.has(instanceKey),

    read(instanceKey) {
      if (!armed.has(instanceKey)) {
        throw new HangarApiError(
          'PERMISSION_REQUIRES_GESTURE',
          'reading the clipboard needs a paste gesture the shell routed to this window',
        );
      }
      const entryId = armed.get(instanceKey);
      // **Spent whatever happens next.** A gesture is one act by the user, not a window in which
      // an app may keep asking.
      armed.delete(instanceKey);

      const entry = entryId === undefined ? ring[0] : ring.find((item) => item.id === entryId);
      if (!entry) {
        throw new HangarApiError('NOT_FOUND', 'there is nothing on the clipboard');
      }
      return { items: entry.payload.items.map((item) => ({ ...item })) };
    },

    clear() {
      ring.length = 0;
      armed.clear();
      announce();
    },

    subscribe(onChange) {
      listeners.add(onChange);
      return () => listeners.delete(onChange);
    },

    attach(target) {
      const onPaste = (): void => {
        const focused = options.focusedInstance?.() ?? null;
        // A paste with nothing focused is a paste into the desktop, which owns no clipboard read.
        if (focused !== null) service.armGesture(focused);
      };
      // Capture, so the shell sees it before an in-process app's own handler can stop it.
      target.addEventListener('paste', onPaste, { capture: true });
      return () => target.removeEventListener('paste', onPaste, { capture: true });
    },
  };

  return service;
}
